import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  CurrentYearMonth,
  GetTrashBinAggregateBusinessSuccessResponseInfo,
  GetTrashBinAggregateBusinessIncludedBusinessSuccessResponseInfo,
  GetTrashBinAllAggregateBusinessSuccessResponseInfo,
  GetTrashBinBusinessSuccessResponseInfo,
  GetTrashBinAllBusinessSuccessResponseInfo,
  UpdateTrashBinAggregateBusinessRequest,
  UpdateTrashBinAggregateBusinessSuccessResponseInfo,
  UpdateTrashBinBusinessRequest,
  UpdateTrashBinBusinessSuccessResponseInfo,
  DeleteTrashBinAggregateBusinessRequest,
  DeleteTrashBinAggregateBusinessSuccessResponseInfo,
  DeleteTrashBinBusinessRequest,
  DeleteTrashBinBusinessSuccessResponseInfo,
  SortType,
  TrashBinApi,
  UseFilterItem,
} from '../axios/generated/trashBinManage';
import { IError } from '../interfaces/IError';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';
import { ITrashBinRepository } from '../interfaces/ITrashBinRepository';

/**
 * ゴミ箱内情報の永続化を担うリポジトリ
 */
export class TrashBinRepository implements ITrashBinRepository {
  /**
   * ゴミ箱内の集計情報の取得
   * @param sortType - ソートタイプ
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param currentYearMonth - 現在の年月
   * @param sortTarget - ソート対象
   * @param filterItem - フィルター
   */
  async getTrashBinAggregateBusiness(
    sortType: SortType,
    limit: number,
    offset: number,
    currentYearMonth: CurrentYearMonth,
    sortTarget: string | undefined,
    filterItem: UseFilterItem = {}
  ): Promise<INetworkResult<GetTrashBinAggregateBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.getTrashBinAggregateBusiness(sortType, limit, offset, currentYearMonth, sortTarget, filterItem);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の集計情報に関連する取引の取得
   * @param sortType - ソートタイプ
   * @param aggregateBusinessId - 集計ID
   * @param currentYearMonth - 現在の年月
   * @param filterItem - フィルター
   */
  async getTrashBinAggregateBusinessIncludedBusiness(
    sortType: SortType,
    aggregateBusinessId: string,
    currentYearMonth: CurrentYearMonth,
    filterItem: UseFilterItem = {}
  ): Promise<INetworkResult<GetTrashBinAggregateBusinessIncludedBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.getTrashBinAggregateBusinessIncludedBusiness(
        sortType,
        aggregateBusinessId,
        currentYearMonth,
        filterItem
      );
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の集計情報の全ID取得
   * @param filterItem - フィルター
   */
  async getTrashBinAllAggregateBusiness(
    filterItem: UseFilterItem = {}
  ): Promise<INetworkResult<GetTrashBinAllAggregateBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.getTrashBinAggregateBusinessAll(filterItem);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の取引情報の取得
   * @param sortType - ソートタイプ
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param currentYearMonth - 現在の年月
   * @param sortTarget - ソート対象
   * @param filterItem - フィルター
   */
  async getTrashBinBusiness(
    sortType: SortType,
    limit: number,
    offset: number,
    currentYearMonth: CurrentYearMonth,
    sortTarget: string | undefined,
    filterItem: UseFilterItem = {}
  ): Promise<INetworkResult<GetTrashBinBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.getTrashBinBusiness(sortType, limit, offset, currentYearMonth, sortTarget, filterItem);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の取引情報の全ID取得
   * @param filterItem - フィルター
   */
  async getTrashBinAllBusiness(filterItem: UseFilterItem = {}): Promise<INetworkResult<GetTrashBinAllBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.getTrashBinBusinessAll(filterItem);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の集計情報のisTrashフラグを更新
   * @param updateTrashBinAggregateBusinessRequest - 更新する集計情報とフラグ
   */
  async updateTrashBinAggregateBusiness(
    updateTrashBinAggregateBusinessRequest: UpdateTrashBinAggregateBusinessRequest
  ): Promise<INetworkResult<UpdateTrashBinAggregateBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.updateTrashBinAggregateBusiness(updateTrashBinAggregateBusinessRequest);
      switch (apiResponse.status) {
        case 200:
          const result: UpdateTrashBinAggregateBusinessSuccessResponseInfo = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の取引情報のisTrashフラグを更新
   * @param updateTrashBinBusinessRequest - 更新する取引情報とフラグ
   */
  async updateTrashBinBusiness(
    updateTrashBinBusinessRequest: UpdateTrashBinBusinessRequest
  ): Promise<INetworkResult<UpdateTrashBinBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.updateTrashBinBusiness(updateTrashBinBusinessRequest);
      switch (apiResponse.status) {
        case 200:
          const result: UpdateTrashBinBusinessSuccessResponseInfo = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の集計情報を完全削除
   * @param deleteTrashBinAggregateBusinessRequest - 削除する集計情報
   */
  async deleteTrashBinAggregateBusiness(
    deleteTrashBinAggregateBusinessRequest: DeleteTrashBinAggregateBusinessRequest
  ): Promise<INetworkResult<DeleteTrashBinAggregateBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.deleteTrashBinAggregateBusiness(deleteTrashBinAggregateBusinessRequest);
      switch (apiResponse.status) {
        case 200:
          const result: DeleteTrashBinAggregateBusinessSuccessResponseInfo = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * ゴミ箱内の取引情報を完全削除
   * @param deleteTrashBinBusinessRequest - 削除する取引情報
   */
  async deleteTrashBinBusiness(
    deleteTrashBinBusinessRequest: DeleteTrashBinBusinessRequest
  ): Promise<INetworkResult<DeleteTrashBinBusinessSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const trashBinApi = new TrashBinApi(undefined, undefined, axiosInstance);

      const apiResponse = await trashBinApi.deleteTrashBinBusiness(deleteTrashBinBusinessRequest);
      switch (apiResponse.status) {
        case 200:
          const result: DeleteTrashBinBusinessSuccessResponseInfo = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return { status: { type: 'error', error: { statusCode: apiResponse.data.statusCode ?? 0, code: '', message: '' } } };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
